import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Tab, Tabs } from '@mui/material';
import { ConfigContext } from 'components/ConfigGuard';
import ExpenseDelegationSection from 'components/expense/ExpenseProfilePage/ExpenseDelegationSection';
import ExpensePaymentOptionsSection from 'components/expense/ExpenseProfilePage/ExpensePaymentOptionsSection';
import TaxFilesSection from 'components/expense/ExpenseProfilePage/TaxFilesSection';
import { ParametersContext } from 'components/ParametersGuard';
import Spinner from 'components/shared/Spinner';
import { useHistory, useLocation } from 'react-router';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import ExpenseService from 'services/api/ExpenseService';
import useRequest from 'hooks/useRequest';
import routes from 'store/configs/Routes';
import { ExpenseProfile } from 'store/types/Expense';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';
import styles from 'components/expense/Expense.module.scss';

const tabConfig = {
  ExpenseProfile: { key: 'ExpenseProfile', label: 'Reimbursement Options', route: routes.expenseProfile } as TabItem,
  ExpenseDelegation: {
    key: 'ExpenseDelegation',
    label: 'Expense Report Delegation',
    route: routes.expenseDelegation,
  } as TabItem,
};

const ExpenseProfilePage: React.FunctionComponent = () => {
  const {
    modulesSettings: {
      expenseProfile: { hiddenTabs, profileTitle },
    },
  } = useContext(ConfigContext);

  const {
    expenseProfile: { profilePageHeader },
  } = useContext(ParametersContext);

  const [spinnerLabel, setSpinnerLabel] = useState('');
  const history = useHistory();
  const { pathname } = useLocation();

  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.ExpenseProfile.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(pathname, visibleTabs));
  }, [history, pathname, visibleTabs]);

  const profileRequest = useCallback(async () => {
    const responseData = await ExpenseService.getExpenseProfile();
    if (responseData) {
      return responseData;
    }
    setSpinnerLabel('Building your Expense Reimbursement Profile...');
    await ExpenseService.createVendor();

    // Add a delay so that NetSuite can finish processing the new vendor record
    await new Promise((resolve) => setTimeout(resolve, 5000));

    setSpinnerLabel('');

    return await ExpenseService.getExpenseProfile();
  }, []);

  const { data, error, loading, refetch } = useRequest<ExpenseProfile>(profileRequest);

  return (
    <>
      {profileTitle && <h1 className={commonStyles.pageTitle}>{profileTitle}</h1>}

      {profilePageHeader && (
        <div className={styles.contentBlock} dangerouslySetInnerHTML={{ __html: profilePageHeader }} />
      )}

      <TabContext value={activeTab}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor={'primary'}
          textColor={'primary'}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
          }}
        >
          {visibleTabs.map((tab) => {
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                label={tab.label}
                className={commonStyles.tab}
                classes={{ selected: commonStyles.activeTab }}
              />
            );
          })}
        </Tabs>
        <TabPanel value={tabConfig.ExpenseProfile.key} className={commonStyles.tabPanel}>
          <Spinner loading={loading} transparent={false} label={spinnerLabel}>
            {error ? (
              <Alert severity={'error'} className={commonStyles.alert}>
                {error}
              </Alert>
            ) : (
              data && <ExpensePaymentOptionsSection data={data} />
            )}
          </Spinner>
        </TabPanel>
        <TabPanel value={tabConfig.ExpenseDelegation.key} className={commonStyles.tabPanel}>
          <Spinner loading={loading} transparent={false}>
            <ExpenseDelegationSection profileId={data?.id ?? ''} />
          </Spinner>
        </TabPanel>
      </TabContext>

      {data && activeTab === tabConfig.ExpenseProfile.key && (
        <div className={styles.contentBorder}>{data && <TaxFilesSection data={data.taxFiles} refetch={refetch} />}</div>
      )}
    </>
  );
};

export default ExpenseProfilePage;
